import { useQuery } from '@tanstack/react-query';
import { getRentedPlaceRequest } from '@/api';
import Button from '@/components/shared/Button';
import Card from '@/components/shared/Card';
import ChatDialog from '@/components/shared/ChatDialog';
import moment from 'moment/moment';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './RentedPropertyPage.scss';
import { PaymentMethod, PaymentPeriod } from '@/types';

const baseClass = 'RentedProperty';

function RentedPropertyPage() {
  const navigate = useNavigate();
  const { unitId } = useParams();
  const [chatId, setChatId] = React.useState<number | null | undefined>(null);
  const {
    data: unit,
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['rentedPlace', unitId],
    queryFn: () => getRentedPlaceRequest(Number(unitId)),
  });

  if (isLoading || isFetching) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <div className="error-status">{error.name}</div>
        <div className="error-message">{error.message}</div>
      </div>
    );
  }

  return (
    <div className={baseClass}>
      <ChatDialog
        onClose={() => {
          setChatId(null);
        }}
        chatId={chatId}
      />
      <Card.Header className={`${baseClass}--header`}>
        <span className={`${baseClass}--header-text`}>
          <span className={`${baseClass}--header-text-prop`}>
            Unit: {unit?.number}
          </span>
        </span>
        <div className={`${baseClass}--header-btns`}>
          <Button
            className={`${baseClass}--header-btn`}
            theme={'secondary'}
            onClick={() => {
              navigate(`/rented`);
            }}
          >
            Back to rented
          </Button>
        </div>
      </Card.Header>
      <Card className={`${baseClass}--information mb-24`}>
        <Card.Header className={`${baseClass}--information-header`}>
          <Card.Title className={`${baseClass}--information-title`}>
            Unit information
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--information-body`}>
          <Card.Container className={`${baseClass}--information-container`}>
            <div className={`${baseClass}--information-top`}>
              <div className={`${baseClass}--information-top-column`}>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Floor of the building
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.floor}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Dimension
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.dimension}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Number of floors
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.numberOfFloors}
                  </div>
                </div>
              </div>
              <div className={`${baseClass}--information-top-column`}>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Rooms
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.rooms}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Bedrooms
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.bedrooms}
                  </div>
                </div>
                <div className={`${baseClass}--information-top-row`}>
                  <div className={`${baseClass}--information-top-row-label`}>
                    Bathrooms
                  </div>
                  <div className={`${baseClass}--information-top-row-value`}>
                    {unit?.bathrooms}
                  </div>
                </div>
              </div>
            </div>
            <div className={`${baseClass}--information-table`}>
              <div className={`${baseClass}--information-table-th`}>
                <div className={`${baseClass}--information-table-th-item`}>
                  ROOM
                </div>
                <div className={`${baseClass}--information-table-th-item`}>
                  FLOOR
                </div>
                <div className={`${baseClass}--information-table-th-item`}>
                  DIMENSION
                </div>
                <div className={`${baseClass}--information-table-th-item`}>
                  FLOORING
                </div>
              </div>

              {(unit?.roomsList || []).map((room: any, index: number) => (
                <div
                  key={index}
                  className={`${baseClass}--information-table-tr`}
                >
                  <div className={`${baseClass}--information-table-cell`}>
                    {room.type}
                  </div>
                  <div className={`${baseClass}--information-table-cell`}>
                    {room.floor}
                  </div>
                  <div className={`${baseClass}--information-table-cell`}>
                    {room.dimension}
                  </div>
                  <div className={`${baseClass}--information-table-cell`}>
                    {room.flooring}
                  </div>
                </div>
              ))}
            </div>
          </Card.Container>
        </Card.Body>
      </Card>
      <Card className={`${baseClass}--inclusions mb-24`}>
        <Card.Header className={`${baseClass}--inclusions-header`}>
          <Card.Title className={`${baseClass}--inclusions-title`}>
            Inclusions
          </Card.Title>
          <Card.TitleDescription
            className={`${baseClass}--inclusions-title-description`}
          >
            Description of leased dwelling, accessories and dependencies.
          </Card.TitleDescription>
        </Card.Header>
        <Card.Body className={`${baseClass}--inclusions-body`}>
          <Card.Container className={`${baseClass}--inclusions-container`}>
            <div className={`${baseClass}--inclusions-row`}>
              <div className={`${baseClass}--inclusions-parking`}>
                <div className={`${baseClass}--inclusions-item`}>
                  {unit?.characteristics.parking.hasOutdoor ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span className={`${baseClass}--inclusions-label`}>
                    Outdoor parking
                  </span>
                </div>
                <div className={`${baseClass}--inclusions-tags`}>
                  {unit?.characteristics.parking.outdoorCount && (
                    <div className={`${baseClass}--inclusions-tags-item`}>
                      {unit?.characteristics.parking.outdoorCount} place
                    </div>
                  )}
                </div>
              </div>
              <div className={`${baseClass}--inclusions-parking`}>
                <div className={`${baseClass}--inclusions-item`}>
                  {unit?.characteristics.parking.hasIndoor ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span className={`${baseClass}--inclusions-label`}>
                    Indoor parking
                  </span>
                </div>
                <div className={`${baseClass}--inclusions-tags`}>
                  {unit?.characteristics.parking.indoorCount && (
                    <div className={`${baseClass}--inclusions-tags-item`}>
                      {unit?.characteristics.parking.indoorCount} place
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={`${baseClass}--inclusions-row`}>
              <div className={`${baseClass}--inclusions-parking`}>
                <div className={`${baseClass}--inclusions-item`}>
                  {unit?.characteristics.storage.hasLockerOrStorage ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span className={`${baseClass}--inclusions-label`}>
                    Locker or storage space
                  </span>
                </div>
                <div className={`${baseClass}--inclusions-tags`}>
                  {unit?.characteristics.storage.storageDescription && (
                    <div className={`${baseClass}--inclusions-tags-item`}>
                      {unit?.characteristics.storage.storageDescription}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={`${baseClass}--inclusions-row`}>
              <div className={`${baseClass}--inclusions-parking`}>
                <div className={`${baseClass}--inclusions-item`}>
                  {unit?.characteristics.furniture.isIncluded ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span className={`${baseClass}--inclusions-label`}>
                    Furniture
                  </span>
                  <span> is leased and included in the rent</span>
                </div>
              </div>
            </div>
            <div className={`${baseClass}--inclusions-appliances`}>
              <div className={`${baseClass}--inclusions-label`}>Appliances</div>
              <div className={`${baseClass}--inclusions-appliances-items`}>
                <div>
                  {unit?.characteristics.furniture.appliances.stove ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span>Stove</span>
                </div>
                <div>
                  {unit?.characteristics.furniture.appliances.dishwasher ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span>Dishwasher</span>
                </div>
                <div>
                  {unit?.characteristics.furniture.appliances.washer ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span>Washer</span>
                </div>
                <div>
                  {unit?.characteristics.furniture.appliances.microwave ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span>Microwave oven</span>
                </div>
                <div>
                  {unit?.characteristics.furniture.appliances.refrigerator ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span>Refrigerator</span>
                </div>
                <div>
                  {unit?.characteristics.furniture.appliances.dryer ? (
                    <i
                      className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                    ></i>
                  ) : (
                    <i
                      className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                    ></i>
                  )}
                  <span>Dryer</span>
                </div>
              </div>
            </div>
            <div className={`${baseClass}--inclusions-furnitures`}>
              <div className={`${baseClass}--inclusions-label`}>Furniture</div>
              <div className={`${baseClass}--inclusions-furnitures-items`}>
                <div className={`${baseClass}--inclusions-furnitures-cell`}>
                  <div className={`${baseClass}--inclusions-furnitures-row`}>
                    {unit?.characteristics.furniture.tables.included ? (
                      <i
                        className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                      ></i>
                    ) : (
                      <i
                        className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                      ></i>
                    )}
                    <span
                      className={`${baseClass}--inclusions-furnitures-row-title`}
                    >
                      Table(s)
                    </span>
                    {unit?.characteristics.furniture.tables.count && (
                      <span
                        className={`${baseClass}--inclusions-furnitures-row-tag`}
                      >
                        {unit?.characteristics.furniture.tables.count}
                      </span>
                    )}
                  </div>
                  <div className={`${baseClass}--inclusions-furnitures-row`}>
                    {unit?.characteristics.furniture.chairs.included ? (
                      <i
                        className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                      ></i>
                    ) : (
                      <i
                        className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                      ></i>
                    )}
                    <span
                      className={`${baseClass}--inclusions-furnitures-row-title`}
                    >
                      Chair(s)
                    </span>
                    {unit?.characteristics.furniture.chairs.count && (
                      <span
                        className={`${baseClass}--inclusions-furnitures-row-tag`}
                      >
                        {unit?.characteristics.furniture.chairs.count}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`${baseClass}--inclusions-furnitures-cell`}>
                  <div className={`${baseClass}--inclusions-furnitures-row`}>
                    {unit?.characteristics.furniture.couches.included ? (
                      <i
                        className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                      ></i>
                    ) : (
                      <i
                        className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                      ></i>
                    )}
                    <span
                      className={`${baseClass}--inclusions-furnitures-row-title`}
                    >
                      Couch(es)
                    </span>
                    {unit?.characteristics.furniture.couches.count && (
                      <span
                        className={`${baseClass}--inclusions-furnitures-row-tag`}
                      >
                        {unit?.characteristics.furniture.couches.count}
                      </span>
                    )}
                  </div>
                  <div className={`${baseClass}--inclusions-furnitures-row`}>
                    {unit?.characteristics.furniture.beds.included ? (
                      <i
                        className={`fa-solid fa-square-check ${baseClass}--inclusions-checked`}
                      ></i>
                    ) : (
                      <i
                        className={`fa-solid fa-xmark ${baseClass}--inclusions-nonchecked`}
                      ></i>
                    )}
                    <span
                      className={`${baseClass}--inclusions-furnitures-row-title`}
                    >
                      Bed(s)
                    </span>
                    {unit?.characteristics.furniture.beds.count && (
                      <span
                        className={`${baseClass}--inclusions-furnitures-row-beds`}
                      >
                        <span
                          className={`${baseClass}--inclusions-furnitures-row-tag`}
                        >
                          {unit?.characteristics.furniture.beds.count}
                        </span>
                        <span
                          className={`${baseClass}--inclusions-furnitures-row-tag`}
                        >
                          {unit?.characteristics.furniture.beds.size}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card.Container>
        </Card.Body>
      </Card>
      <Card className={`${baseClass}--terms mb-24`}>
        <Card.Header className={`${baseClass}--terms-header`}>
          <Card.Title className={`${baseClass}--terms-title`}>
            Terms of lease
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--terms-body`}>
          <Card.Container className={`${baseClass}--terms-container`}>
            {unit?.lease?.isFixed === 'fixed' ? (
              <div className={`${baseClass}--terms-content`}>
                <div className={`${baseClass}--terms-content-title`}>
                  <i className="fa-solid fa-calendar-days"></i>
                  <span className={`${baseClass}--terms-content-title-text`}>
                    Fixed term lease
                  </span>
                </div>
                <div className={`${baseClass}--terms-content-row`}>
                  <span className={`${baseClass}--terms-content-label`}>
                    The term of lease is
                  </span>
                  <span className={`${baseClass}--terms-content-tag`}>
                    {unit?.lease?.period}
                  </span>
                  <span className={`${baseClass}--terms-content-label`}>
                    from
                  </span>
                  <span className={`${baseClass}--terms-content-tag`}>
                    {unit?.lease?.startDate
                      ? moment(unit.lease.startDate).format('YYYY-MM-DD')
                      : 'N/A'}
                  </span>
                  <span className={`${baseClass}--terms-content-label`}>
                    to
                  </span>
                  <span className={`${baseClass}--terms-content-tag`}>
                    {unit?.lease?.endDate
                      ? moment(unit.lease.endDate).format('YYYY-MM-DD')
                      : 'N/A'}
                  </span>
                </div>
              </div>
            ) : (
              <div className={`${baseClass}--terms-content`}>
                <div className={`${baseClass}--terms-content-title`}>
                  <i className="fa-solid fa-calendar-days"></i>
                  <span className={`${baseClass}--terms-content-title-text`}>
                    Indeterminate term lease
                  </span>
                </div>
                <div className={`${baseClass}--terms-content-row`}>
                  <span className={`${baseClass}--terms-content-label`}>
                    Beginning on
                  </span>
                  <span className={`${baseClass}--terms-content-tag`}>
                    {unit?.lease?.indeterminateStartDate
                      ? moment(unit.lease.indeterminateStartDate).format('YYYY-MM-DD')
                      : 'N/A'}
                  </span>
                </div>
              </div>
            )}
          </Card.Container>
        </Card.Body>
      </Card>
      <Card className={`${baseClass}--rent mb-24`}>
        <Card.Header className={`${baseClass}--rent-header`}>
          <Card.Title className={`${baseClass}--rent-title`}>Rent</Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--rent-body`}>
          <Card.Container className={`${baseClass}--rent-container`}>
            <div className={`${baseClass}--rent-price`}>
              <div className={`${baseClass}--rent-price-label`}>
                <i className="fa-solid fa-circle-dollar-to-slot"></i>
                <span>The rent</span>
              </div>
              <div className={`${baseClass}--rent-price-value`}>
                <span className={`${baseClass}--rent-tag`}>
                  {'$ '}
                  {unit?.rentPrice && unit?.rentPrice.toFixed(2)}
                </span>
                <span>
                  per{' '}
                  {unit?.rentPricePeriod === PaymentPeriod.MONTH
                    ? 'month'
                    : 'week'}
                </span>
              </div>
              <div className={`${baseClass}--rent-price-label`}>
                <i className="fa-solid fa-circle-dollar-to-slot"></i>
                <span>The total cost of services</span>
              </div>
              <div className={`${baseClass}--rent-price-value`}>
                <span className={`${baseClass}--rent-tag`}>
                  {'$ '}
                  {unit?.costOfServices && unit?.costOfServices.toFixed(2)}
                </span>
                <span>
                  per{' '}
                  {unit?.costOfServicesPeriod === PaymentPeriod.MONTH
                    ? 'month'
                    : 'week'}
                </span>
              </div>
              <div className={`${baseClass}--rent-price-label`}>
                <i className="fa-solid fa-file-invoice-dollar"></i>
                <span>The total rent</span>
              </div>
              <div className={`${baseClass}--rent-price-value`}>
                <span className={`${baseClass}--rent-tag`}>
                  {'$ '}
                  {unit?.totalRentPrice && unit?.totalRentPrice.toFixed(2)}
                </span>
                <span>
                  per{' '}
                  {unit?.totalRentPricePeriod === PaymentPeriod.MONTH
                    ? 'month'
                    : 'week'}
                </span>
              </div>
            </div>
            <div className={`${baseClass}--rent-terms`}>
              <div className={`${baseClass}--rent-terms-title`}>
                <i className="fa-solid fa-calendar-check"></i>
                <span>First payment period</span>
              </div>
              <div className={`${baseClass}--rent-terms-value`}>
                <span>The rent will be paid on </span>
                <span className={`${baseClass}--terms-content-tag`}>
                  {unit?.firstPaymentDate
                    ? moment(unit.firstPaymentDate).format('YYYY-MM-DD')
                    : 'N/A'}
                </span>
              </div>
            </div>
            <div className={`${baseClass}--rent-terms`}>
              <div className={`${baseClass}--rent-terms-title`}>
                <i className="fa-solid fa-calendar-plus"></i>
                <span>Other payment periods</span>
              </div>
              <div className={`${baseClass}--rent-terms-value`}>
                <span>The rent will be paid on </span>
                <span className={`${baseClass}--terms-content-tag`}>
                  {unit?.paymentFirstDay === PaymentPeriod.MONTH
                    ? '1st day of the month'
                    : '1st day of the week'}
                </span>
              </div>
            </div>
            <div className={`${baseClass}--rent-terms`}>
              <div className={`${baseClass}--rent-terms-title`}>
                <i className="fa-solid fa-money-check-dollar"></i>
                <span>Method of payment</span>
              </div>
              <div className={`${baseClass}--rent-terms-value`}>
                <span>
                  {unit?.paymentMethod === PaymentMethod.CASH && 'Cash'}
                  {unit?.paymentMethod === PaymentMethod.CHEQUE && 'Cheque'}
                  {unit?.paymentMethod === PaymentMethod.ELECTRONIC_TRANSFER &&
                    'Electronic transfer'}
                  {unit?.paymentMethod === PaymentMethod.OTHER && 'Other'}
                </span>
                {unit?.paymentMethod === PaymentMethod.OTHER &&
                  unit?.paymentMethodOtherSpecify && (
                    <span className={`${baseClass}--terms-content-tag`}>
                      {unit?.paymentMethodOtherSpecify}
                    </span>
                  )}
              </div>
            </div>
          </Card.Container>
        </Card.Body>
      </Card>
      <Card className={`${baseClass}--tenant mb-24`}>
        <Card.Header className={`${baseClass}--tenant-header`}>
          <Card.Title className={`${baseClass}--tenant-title`}>
            Owner information
          </Card.Title>
        </Card.Header>
        <Card.Body className={`${baseClass}--tenant-body`}>
          <Card.Container className={`${baseClass}--tenant-container`}>
            <div className={`${baseClass}--tenant-info`}>
              <div className={`${baseClass}--tenant-info-right`}>
                <div className={`${baseClass}--tenant-info-row`}>
                  <span className={`${baseClass}--tenant-info-label`}>
                    Firstname:
                  </span>
                  <span className={`${baseClass}--tenant-info-value`}>
                    {unit?.property?.owner?.firstname}
                  </span>
                </div>
                <div className={`${baseClass}--tenant-info-row`}>
                  <span className={`${baseClass}--tenant-info-label`}>
                    Lastname:
                  </span>
                  <span className={`${baseClass}--tenant-info-value`}>
                    {unit?.property?.owner?.lastname}
                  </span>
                </div>
                <div className={`${baseClass}--tenant-info-row`}>
                  <span className={`${baseClass}--tenant-info-label`}>
                    Email:
                  </span>
                  <a
                    href={`mailto:${unit?.property?.owner?.email}`}
                    className={`${baseClass}--tenant-info-value`}
                  >
                    {unit?.property?.owner?.email}
                  </a>
                </div>
              </div>
              <div>
                <Button
                  className={`${baseClass}--row-cell-profile-btn vacant`}
                  onClick={() => {
                    setChatId(unit?.property?.owner?.id);
                  }}
                >
                  <i className="fa-regular fa-message"></i>
                </Button>
              </div>
            </div>
          </Card.Container>
        </Card.Body>
      </Card>
    </div>
  );
}

export default RentedPropertyPage;